import { Component, OnInit, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from "@angular/material/dialog";
import { ResourceChooserDialogComponent } from "@app/view/shared/resource-chooser-dialog/resource-chooser-dialog.component";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatSelect, MatOption } from "@angular/material/select";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepickerModule
} from "@angular/material/datepicker";
import { MatButton } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { formatDate } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ResourceSkill } from "@app/models/rest/resource-skill";
import { ResourceModel } from "@app/models/rest/resource";
import { Skill } from "@app/models/rest/skill";
import { SharedModule } from "@app/view/shared/shared.module";

@Component({
  selector: "app-resource-to-skill-mapping-dialog",
  template: `
    <h2 mat-dialog-title>Add Resource to Skill</h2>
    <mat-dialog-content>
      <div class="dialog-form">
        <div class="resource-selection">
          @if (!selectedResource) {
            <p class="no-resource">No resource selected</p>
          }
          @if (selectedResource) {
            <div class="selected-resource">
              <strong>{{ selectedResource.name }}</strong>
              @if (selectedResource.skillsList?.length > 0) {
                <p>Skills: {{ getSkillsString(selectedResource) }}</p>
              }
            </div>
          }

          <button
            mat-raised-button
            color="primary"
            (click)="openResourceChooser()"
          >
            {{ selectedResource ? "Change Resource" : "Select Resource" }}
          </button>
        </div>

        <mat-form-field>
          <mat-label>Start Date</mat-label>
          <input
            matInput
            [matDatepicker]="startPicker"
            [(ngModel)]="startDate"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="startPicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field>
          <mat-label>End Date</mat-label>
          <input matInput [matDatepicker]="endPicker" [(ngModel)]="endDate" />
          <mat-datepicker-toggle
            matSuffix
            [for]="endPicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #endPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Mapping Pattern</mat-label>
          <mat-select [(ngModel)]="selectedMappingPattern">
            @for (pattern of data.mappingPatterns; track pattern) {
              <mat-option [value]="pattern">
                {{ pattern.description }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button mat-dialog-close>Cancel</button>
      <button
        mat-button
        color="primary"
        [disabled]="!selectedResource"
        (click)="onSave()"
      >
        Add
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 400px;
      }
      .resource-selection {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 8px;
        padding: 12px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
      }
      .no-resource {
        color: #999;
        font-style: italic;
      }
      .selected-resource {
        margin-bottom: 12px;
      }
    `
  ],
  standalone: true,
  imports: [
    SharedModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatButton,
    MatInput,
    MatDatepickerModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    FormsModule
  ]
})
export class ResourceToSkillMappingDialogComponent {
  dialogRef =
    inject<MatDialogRef<ResourceToSkillMappingDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  data = inject<{
    skill: Skill;
    mappingPatterns: any[];
  }>(MAT_DIALOG_DATA);

  selectedResource: ResourceModel = null;
  selectedMappingPattern: any = null;
  startDate: Date = null;
  endDate: Date = null;

  getSkillsString(resource: ResourceModel): string {
    if (!resource || !resource.skillsList || !resource.skillsList.length) {
      return "";
    }

    return resource.skillsList
      .map((skill) => skill.skill?.description || "")
      .filter((desc) => desc)
      .join(", ");
  }

  openResourceChooser(): void {
    // Open resource chooser dialog with the skill ID as a filter
    const dialogRef = this.dialog.open(ResourceChooserDialogComponent, {
      width: "80%",
      height: "80%"
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedResource = result;
      }
    });
  }

  onSave(): void {
    if (!this.selectedResource) {
      return;
    }

    // Create a new resource skill mapping entry
    const entry = {
      resource: this.selectedResource,
      skill: this.data.skill,
      startDate: this.startDate ? new Date(this.startDate) : null,
      endDate: this.endDate ? new Date(this.endDate) : null,
      mappingPattern: this.selectedMappingPattern
    };

    this.dialogRef.close({
      action: "save",
      entry
    });
  }
}
